import React, { useState } from 'react';

import { uniq } from 'lodash-es';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import BackToPageButton from '@/components/ui/buttons/BackToPageButton';
import Button from '@/components/ui/buttons/Button';
import {
  isCompleteOrganizationMembershipCode,
  ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS,
} from '@/components/ui/formats/organizationMembershipCode';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Input from '@/components/ui/forms/Input';
import LabelWithStatus from '@/components/ui/forms/LabelWithStatus';
import OrganizationMembershipCodeSplitInput from '@/components/ui/forms/OrganizationMembershipCodeSplitInput';
import {
  codeStrictValidation,
  validationSchema,
} from '@/features/registration/schema/validationSchema';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import useOrganizationMembershipValidationV2 from '@/hooks/mypage/useOrganizationMembershipValidationV2';
import {
  type OrganizationMembershipFormValues,
  type OrganizationMembershipProps,
} from '@/interfaces/organizationMembership';

import CSSModule from './OrganizationMembershipRegistrationCreateForm.module.scss';

const RATE_LIMIT_ERROR_STATUS_CODE = 429;
const RATE_LIMIT_ERROR_MESSAGE =
  'リクエスト回数の上限に達しました。しばらく経ってから再度お試しください';

const createValidationSchema = () =>
  Yup.object().shape({
    // 分割入力で「入力途中はエラーを出さない」ため緩い codeStrictValidation を使う。
    // ちょうど規定桁数かは isCodeReadyForOk（送信ボタンの活性）で別途担保する。
    code: codeStrictValidation,
    first_name_kana: validationSchema.fields.first_name_kana,
    last_name_kana: validationSchema.fields.last_name_kana,
  });

const OrganizationMembershipRegistrationCreateForm: React.FC<
  OrganizationMembershipProps
> = ({ formValues, onSubmitForm }) => {
  const navigate = useNavigate();
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  // 一度フォーカスを外したあとは、10桁を入力し切るまで必須エラーを表示し続ける
  const [isCodeRequiredErrorActive, setIsCodeRequiredErrorActive] =
    useState(false);

  const {
    registerWithInnerRef,
    handleSubmit,
    formState: {
      isSubmitting,
      isValid,
      validFields,
      errorMessages,
      dirtyFields,
    },
    getValues,
    reset,
    watch,
    setValue,
  } = useReactHookForm<OrganizationMembershipFormValues>({
    validationSchema: createValidationSchema(),
    defaultValues: formValues,
  });

  const codeValue = watch('code');
  const cleanedCode = codeValue?.replace(/\s/g, '') || '';

  const { isLoading: isLoadingV2, validate } =
    useOrganizationMembershipValidationV2();

  const onSubmitFormWithStudentInfo = async () => {
    const formData = getValues();
    const cleaned = formData.code.replace(/\s/g, '');
    // ボタン無効化（isCodeReadyForOk）と送信条件を一致させ、Enter送信で10桁未満が
    // そのままAPIへ飛ぶのを防ぐ
    if (!isCompleteOrganizationMembershipCode(cleaned)) {
      // 桁数不足のまま Enter 送信されてもボタン無効化を迂回してここに到達する。
      // 何も表示しないとフィードバックが無いため、必須エラーを有効化して理由を示す
      setIsCodeRequiredErrorActive(true);
      return;
    }

    setSubmitErrors([]);
    setServerError(null);

    const { data, error, success } = await validate({
      code: cleaned,
      first_name_kana: formData.first_name_kana || '',
      last_name_kana: formData.last_name_kana || '',
    });

    if (!success && error) {
      if (error.status === RATE_LIMIT_ERROR_STATUS_CODE) {
        reset(getValues(), { keepValues: true });
        setSubmitErrors([RATE_LIMIT_ERROR_MESSAGE]);
      } else if (
        error.status === 404 ||
        error.status === 422 ||
        error.status === 400
      ) {
        reset(getValues(), { keepValues: true });
        setSubmitErrors([
          '入力された団体会員コードが使用できないか、団体会員コードと生徒氏名（かな）が一致しません',
          '内容に誤りがないかもう一度お確かめください',
          '内容に誤りがない場合には学校にお問い合わせください',
        ]);
      } else {
        setServerError(error.json?.message || 'エラーが発生しました。');
      }
      return;
    }

    onSubmitForm(getValues(), data!.student);
  };

  const inputDisabled = isSubmitting || isLoadingV2;

  // submitErrorsがあり、かつフォームが変更されていない場合はボタンを無効化
  const hasDirtyFields = Object.values(dirtyFields).some(Boolean);
  const hasSubmitErrorsWithoutChanges =
    submitErrors.length > 0 && !hasDirtyFields;

  // コード欄が変更済みのときは全フォームエラーを表示。未変更のときはサーバーエラーと重複するメッセージを除外して二重表示を防ぐ
  // フォーカスを外して必須エラーが有効化された後は、10桁になるまで「必須です」を表示する
  const showCodeRequiredError =
    isCodeRequiredErrorActive &&
    cleanedCode.length !== ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS;

  // 空欄時はスキーマの必須エラーと showCodeRequiredError が同じ文言を出すため uniq で重複を除去する
  const formErrors = uniq([
    ...(dirtyFields.code
      ? [...(errorMessages.code || [])]
      : [...(errorMessages.code || [])].filter(
          (error) => !submitErrors.includes(error as string),
        )),
    ...(showCodeRequiredError ? ['団体会員コードは必須です'] : []),
  ]);

  const isCodeReadyForOk =
    isCompleteOrganizationMembershipCode(cleanedCode) && !formErrors.length;

  // codeStrictValidation は桁数を検証しない（10桁未満でも isValid が立つ）ため、
  // 送信可否は10桁ちょうど入力済みか（isCodeReadyForOk）でも判定する
  const buttonDisabled =
    !isValid ||
    isSubmitting ||
    isLoadingV2 ||
    hasSubmitErrorsWithoutChanges ||
    !isCodeReadyForOk;

  const firstNameKanaErrors = [...(errorMessages.first_name_kana || [])].filter(
    (error) => !submitErrors.includes(error as string),
  );

  const lastNameKanaErrors = [...(errorMessages.last_name_kana || [])].filter(
    (error) => !submitErrors.includes(error as string),
  );

  const onGoBack = () => navigate('/mypage/profile');
  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(() => void onSubmitFormWithStudentInfo())}
      className={CSSModule.OrganizationMembershipRegistrationCreateForm}
    >
      <div
        className={
          CSSModule.OrganizationMembershipRegistrationCreateForm__StepDescription
        }
      >
        <p>団体会員コードと生徒氏名（かな）を入力してください。</p>
      </div>
      <FormGroup>
        <LabelWithStatus required valid={isCodeReadyForOk}>
          団体会員コード
        </LabelWithStatus>
        <Field
          className={
            CSSModule.OrganizationMembershipRegistrationCreateForm__Field
          }
        >
          <OrganizationMembershipCodeSplitInput
            value={codeValue}
            onChange={(value) =>
              setValue('code', value, {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
              })
            }
            onBlur={() => {
              // フォーカスが外れた時点で10桁未満なら、以降10桁を入力し切るまで必須エラーを表示する。
              // 「使用できません」は入力時にスキーマ側で表示されるため、ここでは関与しない。
              if (
                getValues('code').replace(/\s/g, '').length !==
                ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS
              ) {
                setIsCodeRequiredErrorActive(true);
              }
            }}
            disabled={inputDisabled}
            name="code"
          />
          <ErrorList errors={formErrors} />
          <ul
            className={
              CSSModule.OrganizationMembershipRegistrationCreateForm__FieldComment
            }
          >
            <li>
              <p>
                学校から配布された団体会員コードを<b>半角数字</b>
                で入力してください。
              </p>
            </li>
            <li>
              <p>
                団体会員コードは１つにつき１アカウントまで登録可能です。複数のアカウントを登録することはできません。
              </p>
            </li>
          </ul>
        </Field>
      </FormGroup>
      <FormGroup
        className={
          CSSModule.OrganizationMembershipRegistrationCreateForm__StudentNameFormGroup
        }
      >
        <LabelWithStatus
          required
          valid={validFields.last_name_kana && validFields.first_name_kana}
        >
          生徒氏名（かな）
        </LabelWithStatus>
        <Field
          className={
            CSSModule.OrganizationMembershipRegistrationCreateForm__Field
          }
        >
          <div
            className={
              CSSModule.OrganizationMembershipRegistrationCreateForm__HorizontalInput
            }
          >
            <div
              className={
                CSSModule.OrganizationMembershipRegistrationCreateForm__HorizontalInputItem
              }
            >
              <Input
                {...registerWithInnerRef('last_name_kana')}
                disabled={inputDisabled}
                placeholder="例）やまだ"
              />
            </div>
            <div
              className={
                CSSModule.OrganizationMembershipRegistrationCreateForm__HorizontalInputItem
              }
            >
              <Input
                {...registerWithInnerRef('first_name_kana')}
                disabled={inputDisabled}
                placeholder="例）はなこ"
              />
            </div>
          </div>
          <ErrorList errors={[...lastNameKanaErrors, ...firstNameKanaErrors]} />
          {submitErrors.length > 0 &&
            !dirtyFields.first_name_kana &&
            !dirtyFields.last_name_kana && <ErrorList errors={submitErrors} />}
          <ul
            className={
              CSSModule.OrganizationMembershipRegistrationCreateForm__FieldComment
            }
          >
            <li>
              <p>
                学校から配布された用紙に記載の生徒氏名（かな）を入力してください。
              </p>
            </li>
            <li>
              <p>
                生徒氏名（かな）が分からない場合は、
                <a
                  href="https://studysapuri-school.jp/studentfaq/s/article/000002480"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ヘルプページ
                </a>
                をご確認ください。
              </p>
            </li>
          </ul>
        </Field>
      </FormGroup>
      {serverError && <ErrorList errors={[serverError]} />}
      <Button
        type="submit"
        color="primary"
        disabled={buttonDisabled}
        className={
          CSSModule.OrganizationMembershipRegistrationCreateForm__Button
        }
      >
        確認画面へ
      </Button>
      <BackToPageButton
        onClick={onGoBack}
        className={
          CSSModule.OrganizationMembershipRegistrationCreateForm__BackToPageButton
        }
      >
        ＜戻る
      </BackToPageButton>
    </form>
  );
};

export default OrganizationMembershipRegistrationCreateForm;
